/**
 * Hook for collaborative adventure editing
 * Syncs adventure field changes between editors in real-time
 * Also persists criteria changes to the API
 */

import { useEffect, useCallback, useRef } from 'react'
import { useAdventureCollaboration } from '@contexts/AdventureCollaborationContext'
import { useAdventureEditorStore } from '@features/adventures/stores/adventureEditorStore'
import { adventuresApi } from '@api/endpoints/adventures'
import type { AdventureGeneralInfoDto, CriteriaItem, CriteriaLogicGroup } from '@features/adventures/types/adventure-editor.types'
import { type WorkflowConditionType, getDefaultConditionConfig, type DefaultConditionConfigOptions } from '@features/adventures/types/workflow.types'

// Fields that should be synced in real-time
type SyncableGeneralInfoField = keyof AdventureGeneralInfoDto
type SyncableAdventureField = 'tags' | 'useDisplayForJoining'

// Combined type for all syncable fields
type SyncableField = SyncableGeneralInfoField | SyncableAdventureField

interface UseCollaborativeAdventureEditorOptions {
  /** Whether to enable collaboration (default: true) */
  enabled?: boolean
  /** Debounce delay for text fields in ms (default: 300) */
  debounceMs?: number
}

/**
 * Hook that provides collaborative editing for adventure fields.
 * - Sends local changes to other editors
 * - Applies remote changes to the local store
 */
export function useCollaborativeAdventureEditor(options: UseCollaborativeAdventureEditorOptions = {}) {
  const { enabled = true, debounceMs = 300 } = options

  const {
    isConnected,
    adventureId,
    sendAdventureFieldUpdate,
    sendChange,
    subscribeToRemoteChanges,
  } = useAdventureCollaboration()

  // Track whether we're applying a remote change to prevent feedback loops
  const isApplyingRemoteChangeRef = useRef(false)

  // Debounce timers for text fields
  const debounceTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  // Get store actions
  const updateGeneralInfo = useAdventureEditorStore((state) => state.updateGeneralInfo)
  const updateAdventure = useAdventureEditorStore((state) => state.updateAdventure)
  const setUseDisplayForJoining = useAdventureEditorStore((state) => state.setUseDisplayForJoining)
  const storeToggleTag = useAdventureEditorStore((state) => state.toggleTag)
  const storeRemoveTag = useAdventureEditorStore((state) => state.removeTag)

  // Criteria store actions
  const storeAddCriteriaItem = useAdventureEditorStore((state) => state.addCriteriaItem)
  const storeUpdateCriteriaItem = useAdventureEditorStore((state) => state.updateCriteriaItem)
  const storeRemoveCriteriaItem = useAdventureEditorStore((state) => state.removeCriteriaItem)
  const storeAddCriteriaLogicGroup = useAdventureEditorStore((state) => state.addCriteriaLogicGroup)
  const storeUpdateCriteriaLogicGroup = useAdventureEditorStore((state) => state.updateCriteriaLogicGroup)
  const storeRemoveCriteriaLogicGroup = useAdventureEditorStore((state) => state.removeCriteriaLogicGroup)

  // For receiving remote criteria - we need direct state setters
  const setDisplayCriteriaItems = useCallback((items: CriteriaItem[]) => {
    useAdventureEditorStore.setState({ displayCriteriaItems: items })
  }, [])
  const setJoinCriteriaItems = useCallback((items: CriteriaItem[]) => {
    useAdventureEditorStore.setState({ joinCriteriaItems: items })
  }, [])
  const setDisplayCriteriaLogicGroups = useCallback((groups: CriteriaLogicGroup[]) => {
    useAdventureEditorStore.setState({ displayCriteriaLogicGroups: groups })
  }, [])
  const setJoinCriteriaLogicGroups = useCallback((groups: CriteriaLogicGroup[]) => {
    useAdventureEditorStore.setState({ joinCriteriaLogicGroups: groups })
  }, [])

  // Subscribe to remote adventure field and criteria changes
  useEffect(() => {
    if (!enabled) return

    const unsubscribe = subscribeToRemoteChanges((change) => {
      const changeType = change.changeData.type

      // Handle adventure field updates
      if (changeType === 'adventure_field_update') {
        const { field, value } = change.changeData as { type: 'adventure_field_update'; field: string; value: unknown }
        console.log('[CollaborativeEditor] Received remote field update:', field, value)

        isApplyingRemoteChangeRef.current = true

        if (isGeneralInfoField(field)) {
          updateGeneralInfo({ [field]: value })
        } else if (field === 'tags') {
          updateAdventure({ tags: value as string[] })
        } else if (field === 'useDisplayForJoining') {
          setUseDisplayForJoining(value as boolean)
        } else if (field === 'thumbnailImageUrl') {
          // Handle null as undefined for clearing the image
          // Also clear the ID when clearing the URL
          if (value) {
            updateAdventure({ thumbnailImageUrl: value as string })
          } else {
            updateAdventure({ thumbnailImageUrl: undefined, thumbnailImageId: undefined })
          }
        } else if (field === 'coverImageUrl') {
          // Handle null as undefined for clearing the image
          // Also clear the ID when clearing the URL
          if (value) {
            updateAdventure({ coverImageUrl: value as string })
          } else {
            updateAdventure({ coverImageUrl: undefined, coverImageId: undefined })
          }
        }

        queueMicrotask(() => {
          isApplyingRemoteChangeRef.current = false
        })
        return
      }

      // Handle criteria changes
      if (changeType === 'criteria_add') {
        const { criteriaType, item } = change.changeData as {
          type: 'criteria_add'
          criteriaType: 'display' | 'join'
          item: CriteriaItem
        }
        console.log('[CollaborativeEditor] Received remote criteria add:', criteriaType, item)

        // Validate that conditionType is present
        if (item.conditionType === undefined) {
          console.error('[CollaborativeEditor] Remote criteria add missing conditionType:', item)
        }

        isApplyingRemoteChangeRef.current = true

        const state = useAdventureEditorStore.getState()
        const itemsKey = criteriaType === 'display' ? 'displayCriteriaItems' : 'joinCriteriaItems'
        const currentItems = state[itemsKey]

        // Only add if not already present
        if (!currentItems.some(i => i.id === item.id)) {
          if (criteriaType === 'display') {
            setDisplayCriteriaItems([...currentItems, item])
          } else {
            setJoinCriteriaItems([...currentItems, item])
          }
        }

        queueMicrotask(() => {
          isApplyingRemoteChangeRef.current = false
        })
        return
      }

      if (changeType === 'criteria_update') {
        const { criteriaType, itemId, config } = change.changeData as {
          type: 'criteria_update'
          criteriaType: 'display' | 'join'
          itemId: string
          config: Record<string, unknown>
        }
        console.log('[CollaborativeEditor] Received remote criteria update:', criteriaType, itemId)

        isApplyingRemoteChangeRef.current = true

        const state = useAdventureEditorStore.getState()
        const itemsKey = criteriaType === 'display' ? 'displayCriteriaItems' : 'joinCriteriaItems'
        const updatedItems = state[itemsKey].map(item =>
          item.id === itemId ? { ...item, config } : item
        )

        if (criteriaType === 'display') {
          setDisplayCriteriaItems(updatedItems)
        } else {
          setJoinCriteriaItems(updatedItems)
        }

        queueMicrotask(() => {
          isApplyingRemoteChangeRef.current = false
        })
        return
      }

      if (changeType === 'criteria_delete') {
        const { criteriaType, itemId } = change.changeData as {
          type: 'criteria_delete'
          criteriaType: 'display' | 'join'
          itemId: string
        }
        console.log('[CollaborativeEditor] Received remote criteria delete:', criteriaType, itemId)

        isApplyingRemoteChangeRef.current = true

        const state = useAdventureEditorStore.getState()
        const itemsKey = criteriaType === 'display' ? 'displayCriteriaItems' : 'joinCriteriaItems'
        const filteredItems = state[itemsKey].filter(item => item.id !== itemId)

        if (criteriaType === 'display') {
          setDisplayCriteriaItems(filteredItems)
        } else {
          setJoinCriteriaItems(filteredItems)
        }

        queueMicrotask(() => {
          isApplyingRemoteChangeRef.current = false
        })
        return
      }

      if (changeType === 'criteria_logic_group_add') {
        const { criteriaType, group } = change.changeData as {
          type: 'criteria_logic_group_add'
          criteriaType: 'display' | 'join'
          group: CriteriaLogicGroup
        }
        console.log('[CollaborativeEditor] Received remote logic group add:', criteriaType, group)

        isApplyingRemoteChangeRef.current = true

        const state = useAdventureEditorStore.getState()
        const groupsKey = criteriaType === 'display' ? 'displayCriteriaLogicGroups' : 'joinCriteriaLogicGroups'
        const currentGroups = state[groupsKey]

        if (!currentGroups.some(g => g.id === group.id)) {
          if (criteriaType === 'display') {
            setDisplayCriteriaLogicGroups([...currentGroups, group])
          } else {
            setJoinCriteriaLogicGroups([...currentGroups, group])
          }
        }

        queueMicrotask(() => {
          isApplyingRemoteChangeRef.current = false
        })
        return
      }

      if (changeType === 'criteria_logic_group_update') {
        const { criteriaType, groupId, isAnd } = change.changeData as {
          type: 'criteria_logic_group_update'
          criteriaType: 'display' | 'join'
          groupId: string
          isAnd: boolean
        }
        console.log('[CollaborativeEditor] Received remote logic group update:', criteriaType, groupId, isAnd)

        isApplyingRemoteChangeRef.current = true

        const state = useAdventureEditorStore.getState()
        const groupsKey = criteriaType === 'display' ? 'displayCriteriaLogicGroups' : 'joinCriteriaLogicGroups'
        const updatedGroups = state[groupsKey].map(group =>
          group.id === groupId ? { ...group, isAnd } : group
        )

        if (criteriaType === 'display') {
          setDisplayCriteriaLogicGroups(updatedGroups)
        } else {
          setJoinCriteriaLogicGroups(updatedGroups)
        }

        queueMicrotask(() => {
          isApplyingRemoteChangeRef.current = false
        })
        return
      }

      if (changeType === 'criteria_logic_group_delete') {
        const { criteriaType, groupId } = change.changeData as {
          type: 'criteria_logic_group_delete'
          criteriaType: 'display' | 'join'
          groupId: string
        }
        console.log('[CollaborativeEditor] Received remote logic group delete:', criteriaType, groupId)

        isApplyingRemoteChangeRef.current = true

        const state = useAdventureEditorStore.getState()
        const groupsKey = criteriaType === 'display' ? 'displayCriteriaLogicGroups' : 'joinCriteriaLogicGroups'
        const filteredGroups = state[groupsKey].filter(group => group.id !== groupId)

        if (criteriaType === 'display') {
          setDisplayCriteriaLogicGroups(filteredGroups)
        } else {
          setJoinCriteriaLogicGroups(filteredGroups)
        }

        queueMicrotask(() => {
          isApplyingRemoteChangeRef.current = false
        })
        return
      }
    })

    return unsubscribe
  }, [enabled, subscribeToRemoteChanges, updateGeneralInfo, updateAdventure, setUseDisplayForJoining,
      setDisplayCriteriaItems, setJoinCriteriaItems, setDisplayCriteriaLogicGroups, setJoinCriteriaLogicGroups])

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      debounceTimersRef.current.forEach(timer => clearTimeout(timer))
      debounceTimersRef.current.clear()
    }
  }, [])

  /**
   * Update a general info field and broadcast to other editors
   */
  const updateGeneralInfoField = useCallback(<K extends keyof AdventureGeneralInfoDto>(
    field: K,
    value: AdventureGeneralInfoDto[K],
    options?: { immediate?: boolean }
  ) => {
    // Update local store immediately
    updateGeneralInfo({ [field]: value })

    // Don't broadcast if disabled, not connected, or if this is a remote change
    if (!enabled || !isConnected || isApplyingRemoteChangeRef.current) return

    // Clear existing debounce timer for this field
    const existingTimer = debounceTimersRef.current.get(field)
    if (existingTimer) {
      clearTimeout(existingTimer)
    }

    // Determine if we should debounce (for text fields)
    const shouldDebounce = !options?.immediate && isTextFieldValue(value)

    if (shouldDebounce) {
      // Debounce text field updates
      const timer = setTimeout(() => {
        sendAdventureFieldUpdate(field, value)
        debounceTimersRef.current.delete(field)
      }, debounceMs)
      debounceTimersRef.current.set(field, timer)
    } else {
      // Send immediately for non-text fields
      sendAdventureFieldUpdate(field, value)
    }
  }, [enabled, isConnected, updateGeneralInfo, sendAdventureFieldUpdate, debounceMs])

  /**
   * Update tags and broadcast to other editors
   */
  const updateTags = useCallback((tags: string[]) => {
    updateAdventure({ tags })

    if (!enabled || !isConnected || isApplyingRemoteChangeRef.current) return
    sendAdventureFieldUpdate('tags', tags)
  }, [enabled, isConnected, updateAdventure, sendAdventureFieldUpdate])

  /**
   * Update useDisplayForJoining, persist to API, and broadcast
   */
  const updateUseDisplayForJoining = useCallback(async (value: boolean) => {
    // Skip if this is from a remote change
    if (isApplyingRemoteChangeRef.current) return

    // Update local store immediately
    setUseDisplayForJoining(value)

    // Persist to API if we have an adventure ID
    if (adventureId) {
      try {
        await adventuresApi.setUseDisplayForJoining(adventureId, value)
        console.log('[Criteria] Updated useDisplayForJoining:', value)
      } catch (error) {
        console.error('[Criteria] Failed to update useDisplayForJoining:', error)
      }
    }

    // Broadcast to other editors
    if (enabled && isConnected) {
      sendAdventureFieldUpdate('useDisplayForJoining', value)
    }
  }, [adventureId, enabled, isConnected, setUseDisplayForJoining, sendAdventureFieldUpdate])

  /**
   * Toggle a tag and broadcast to other editors
   */
  const toggleTag = useCallback((tagId: string, selectionMode: 'single' | 'multiple', categoryId: string) => {
    // Get current tags before toggle
    const adventure = useAdventureEditorStore.getState().adventure
    const currentTags = adventure?.tags || []

    // Toggle locally
    storeToggleTag(tagId, selectionMode, categoryId)

    if (!enabled || !isConnected || isApplyingRemoteChangeRef.current) return

    // Get updated tags after toggle
    const updatedAdventure = useAdventureEditorStore.getState().adventure
    const newTags = updatedAdventure?.tags || []

    // Send the updated tags array
    sendAdventureFieldUpdate('tags', newTags)
  }, [enabled, isConnected, storeToggleTag, sendAdventureFieldUpdate])

  /**
   * Remove a tag and broadcast to other editors
   */
  const removeTag = useCallback((tagId: string) => {
    // Remove locally
    storeRemoveTag(tagId)

    if (!enabled || !isConnected || isApplyingRemoteChangeRef.current) return

    // Get updated tags after removal
    const adventure = useAdventureEditorStore.getState().adventure
    const newTags = adventure?.tags || []

    // Send the updated tags array
    sendAdventureFieldUpdate('tags', newTags)
  }, [enabled, isConnected, storeRemoveTag, sendAdventureFieldUpdate])

  /**
   * Flush any pending debounced updates immediately
   */
  const flushPendingUpdates = useCallback(() => {
    debounceTimersRef.current.forEach((timer, field) => {
      clearTimeout(timer)
      // Note: This doesn't actually send - the component should save before navigating away
    })
    debounceTimersRef.current.clear()
  }, [])

  // ========================
  // CRITERIA OPERATIONS
  // ========================

  /**
   * Add a criteria item, persist to API, and broadcast to other editors
   * logicGroupId is now required by the API
   */
  const addCriteriaItem = useCallback(async (
    type: 'display' | 'join',
    conditionType: WorkflowConditionType,
    logicGroupId: string
  ) => {
    // Skip if this is from a remote change
    if (isApplyingRemoteChangeRef.current) return

    // Get the current order for the new item
    const state = useAdventureEditorStore.getState()
    const items = type === 'display' ? state.displayCriteriaItems : state.joinCriteriaItems
    const maxOrder = items.length > 0 ? Math.max(...items.map(i => i.order)) : -1

    // Call API to persist the criterion
    if (adventureId) {
      try {
        const apiCall = type === 'display'
          ? adventuresApi.addDisplayCriterion
          : adventuresApi.addJoinCriterion

        // Get adventure location and geocache code for default config
        const state = useAdventureEditorStore.getState()
        const generalInfo = state.generalInfo
        const adventure = state.adventure
        const configOptions: DefaultConditionConfigOptions = {
          adventureLatitude: generalInfo.latitude,
          adventureLongitude: generalInfo.longitude,
          geocacheCode: adventure?.geocacheCode || '',
        }

        // Get default config for this condition type so it has sensible defaults
        const defaultConfig = getDefaultConditionConfig(conditionType, configOptions)

        const response = await apiCall(adventureId, {
          conditionType,
          config: defaultConfig,
          logicGroupId, // Required by API
          order: maxOrder + 1,
        })

        console.log('[Criteria] API response for add:', response)

        // Add to local store with the server-generated ID
        // Note: API may return just id/priority or the full config object
        const itemsKey = type === 'display' ? 'displayCriteriaItems' : 'joinCriteriaItems'
        const newItem: CriteriaItem = {
          id: response.id,
          // Use the conditionType we sent since API response may not include it
          conditionType: (response.conditionType as WorkflowConditionType) ?? conditionType,
          config: response.config ?? response, // API might return config as the response itself
          logicGroupId,
          order: response.priority ?? 0, // API uses "priority" for ordering
        }

        useAdventureEditorStore.setState({
          [itemsKey]: [...items, newItem],
          isDirty: true,
        })

        // Broadcast to other editors
        if (enabled && isConnected) {
          sendChange({
            type: 'criteria_add',
            criteriaType: type,
            item: {
              id: newItem.id,
              conditionType: conditionType, // Use original conditionType, not from response
              config: newItem.config,
              logicGroupId: newItem.logicGroupId,
              order: newItem.order,
            },
          })
        }

        console.log(`[Criteria] Added ${type} criterion:`, response.id)
      } catch (error) {
        console.error(`[Criteria] Failed to add ${type} criterion:`, error)
        // Optionally show error to user
      }
    } else {
      // No adventure ID - just add locally (new adventure case)
      storeAddCriteriaItem(type, conditionType, logicGroupId)
    }
  }, [adventureId, enabled, isConnected, storeAddCriteriaItem, sendChange])

  /**
   * Update a criteria item's config, persist to API, and broadcast to other editors
   */
  const updateCriteriaItem = useCallback((
    type: 'display' | 'join',
    itemId: string,
    config: Record<string, unknown>
  ) => {
    // Skip if this is from a remote change
    if (isApplyingRemoteChangeRef.current) return

    // Update local store immediately for responsive UI
    storeUpdateCriteriaItem(type, itemId, config)

    // Debounce API calls and broadcasts
    const timerKey = `criteria_${type}_${itemId}`
    const existingTimer = debounceTimersRef.current.get(timerKey)
    if (existingTimer) {
      clearTimeout(existingTimer)
    }

    const timer = setTimeout(async () => {
      debounceTimersRef.current.delete(timerKey)

      // Persist to API if we have an adventure ID
      if (adventureId) {
        try {
          const apiCall = type === 'display'
            ? adventuresApi.updateDisplayCriterion
            : adventuresApi.updateJoinCriterion

          // Strip server-side metadata fields before sending to API
          const cleanConfig = stripCriteriaMetadata(config)
          console.log(`[Criteria] Updating ${type} criterion:`, itemId, 'with config:', cleanConfig)
          await apiCall(adventureId, itemId, { config: cleanConfig })
          console.log(`[Criteria] Updated ${type} criterion:`, itemId)
        } catch (error) {
          console.error(`[Criteria] Failed to update ${type} criterion:`, error)
        }
      }

      // Broadcast to other editors
      if (enabled && isConnected) {
        sendChange({
          type: 'criteria_update',
          criteriaType: type,
          itemId,
          config,
        })
      }
    }, debounceMs)
    debounceTimersRef.current.set(timerKey, timer)
  }, [adventureId, enabled, isConnected, storeUpdateCriteriaItem, sendChange, debounceMs])

  /**
   * Remove a criteria item, persist to API, and broadcast to other editors
   */
  const removeCriteriaItem = useCallback(async (
    type: 'display' | 'join',
    itemId: string
  ) => {
    // Skip if this is from a remote change
    if (isApplyingRemoteChangeRef.current) return

    // Remove from local store immediately
    storeRemoveCriteriaItem(type, itemId)

    // Persist to API if we have an adventure ID
    if (adventureId) {
      try {
        const apiCall = type === 'display'
          ? adventuresApi.deleteDisplayCriterion
          : adventuresApi.deleteJoinCriterion

        await apiCall(adventureId, itemId)
        console.log(`[Criteria] Deleted ${type} criterion:`, itemId)
      } catch (error) {
        console.error(`[Criteria] Failed to delete ${type} criterion:`, error)
      }
    }

    // Broadcast to other editors
    if (enabled && isConnected) {
      sendChange({
        type: 'criteria_delete',
        criteriaType: type,
        itemId,
      })
    }
  }, [adventureId, enabled, isConnected, storeRemoveCriteriaItem, sendChange])

  /**
   * Add a logic group, persist to API, and broadcast to other editors
   * Returns the new group ID (or undefined if failed)
   */
  const addCriteriaLogicGroup = useCallback(async (
    type: 'display' | 'join',
    operator: 'AND' | 'OR' = 'AND'
  ): Promise<string | undefined> => {
    // Skip if this is from a remote change
    if (isApplyingRemoteChangeRef.current) return undefined

    const isAnd = operator === 'AND'

    // Call API to persist the logic group
    if (adventureId) {
      try {
        const apiCall = type === 'display'
          ? adventuresApi.createDisplayCriteriaGroup
          : adventuresApi.createJoinCriteriaGroup

        const response = await apiCall(adventureId, { isAnd })

        // Add to local store with the server-generated ID
        const state = useAdventureEditorStore.getState()
        const groupsKey = type === 'display' ? 'displayCriteriaLogicGroups' : 'joinCriteriaLogicGroups'
        const currentGroups = state[groupsKey]

        const newGroup: CriteriaLogicGroup = {
          id: response.id,
          isAnd: response.isAnd,
          orderIndex: response.orderIndex,
        }

        useAdventureEditorStore.setState({
          [groupsKey]: [...currentGroups, newGroup],
          isDirty: true,
        })

        // Broadcast to other editors
        if (enabled && isConnected) {
          sendChange({
            type: 'criteria_logic_group_add',
            criteriaType: type,
            group: {
              id: newGroup.id,
              isAnd: newGroup.isAnd,
              orderIndex: newGroup.orderIndex,
            },
          })
        }

        console.log(`[Criteria] Added ${type} logic group:`, response.id)
        return response.id
      } catch (error) {
        console.error(`[Criteria] Failed to add ${type} logic group:`, error)
        return undefined
      }
    } else {
      // No adventure ID - just add locally (new adventure case)
      const groupId = storeAddCriteriaLogicGroup(type, operator)
      return groupId
    }
  }, [adventureId, enabled, isConnected, storeAddCriteriaLogicGroup, sendChange])

  /**
   * Update a logic group's operator, persist to API, and broadcast to other editors
   */
  const updateCriteriaLogicGroup = useCallback(async (
    type: 'display' | 'join',
    groupId: string,
    operator: 'AND' | 'OR'
  ) => {
    // Skip if this is from a remote change
    if (isApplyingRemoteChangeRef.current) return

    const isAnd = operator === 'AND'

    // Update local store immediately for responsive UI
    storeUpdateCriteriaLogicGroup(type, groupId, operator)

    // Persist to API if we have an adventure ID
    if (adventureId) {
      try {
        const apiCall = type === 'display'
          ? adventuresApi.updateDisplayCriteriaGroup
          : adventuresApi.updateJoinCriteriaGroup

        await apiCall(adventureId, groupId, { isAnd })
        console.log(`[Criteria] Updated ${type} logic group:`, groupId)
      } catch (error) {
        console.error(`[Criteria] Failed to update ${type} logic group:`, error)
      }
    }

    // Broadcast to other editors
    if (enabled && isConnected) {
      sendChange({
        type: 'criteria_logic_group_update',
        criteriaType: type,
        groupId,
        isAnd,
      })
    }
  }, [adventureId, enabled, isConnected, storeUpdateCriteriaLogicGroup, sendChange])

  /**
   * Remove a logic group, persist to API, and broadcast to other editors
   */
  const removeCriteriaLogicGroup = useCallback(async (
    type: 'display' | 'join',
    groupId: string
  ) => {
    // Skip if this is from a remote change
    if (isApplyingRemoteChangeRef.current) return

    // Remove from local store immediately
    storeRemoveCriteriaLogicGroup(type, groupId)

    // Persist to API if we have an adventure ID
    if (adventureId) {
      try {
        const apiCall = type === 'display'
          ? adventuresApi.deleteDisplayCriteriaGroup
          : adventuresApi.deleteJoinCriteriaGroup

        await apiCall(adventureId, groupId)
        console.log(`[Criteria] Deleted ${type} logic group:`, groupId)
      } catch (error) {
        console.error(`[Criteria] Failed to delete ${type} logic group:`, error)
      }
    }

    // Broadcast to other editors
    if (enabled && isConnected) {
      sendChange({
        type: 'criteria_logic_group_delete',
        criteriaType: type,
        groupId,
      })
    }
  }, [adventureId, enabled, isConnected, storeRemoveCriteriaLogicGroup, sendChange])

  return {
    isConnected,
    updateGeneralInfoField,
    updateTags,
    updateUseDisplayForJoining,
    toggleTag,
    removeTag,
    flushPendingUpdates,
    isApplyingRemoteChange: () => isApplyingRemoteChangeRef.current,
    // Criteria operations
    addCriteriaItem,
    updateCriteriaItem,
    removeCriteriaItem,
    addCriteriaLogicGroup,
    updateCriteriaLogicGroup,
    removeCriteriaLogicGroup,
  }
}

// Server-side metadata fields that should not be sent back when updating criteria config
const CRITERIA_METADATA_FIELDS = [
  'id',
  'targetType',
  'targetId',
  'category',
  'isActive',
  'createdAt',
  'createdByUserId',
  'updatedAt',
  'updatedByUserId',
  'priority',
  'logicGroupId',
  'isInLogicGroup',
  'canBeMoved',
] as const

/**
 * Strips server-side metadata fields from a criteria config before sending to API
 */
function stripCriteriaMetadata(config: Record<string, unknown>): Record<string, unknown> {
  const cleanConfig: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(config)) {
    if (!CRITERIA_METADATA_FIELDS.includes(key as typeof CRITERIA_METADATA_FIELDS[number])) {
      cleanConfig[key] = value
    }
  }
  return cleanConfig
}

// Helper functions
function isGeneralInfoField(field: string): field is keyof AdventureGeneralInfoDto {
  const generalInfoFields: (keyof AdventureGeneralInfoDto)[] = [
    'title',
    'quickDescription',
    'fullDescription',
    'mainCategory',
    'startDate',
    'endDate',
    'latitude',
    'longitude',
    'isPublic',
    'allowGroups',
    'maxParticipants',
    'isRepeatable',
    'difficultyLevel',
  ]
  return generalInfoFields.includes(field as keyof AdventureGeneralInfoDto)
}

function isTextFieldValue(value: unknown): boolean {
  return typeof value === 'string'
}
